// PDF support for the invoice scanner.
// Extracts the PDF's own text layer WITH word positions (x/y), building the
// same "blocks" structure the image OCR produces — so the existing table
// header detection / column mapping logic works unchanged and far more
// accurately than OCR. A page image is also rendered for preview.

let pdfjsLib = null;
let workerConfigured = false;

async function getPdfLib() {
  if (!pdfjsLib) {
    pdfjsLib = await import('pdfjs-dist');
  }
  if (!workerConfigured) {
    pdfjsLib.GlobalWorkerOptions.workerSrc =
      `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
    workerConfigured = true;
  }
  return pdfjsLib;
}

// Group PDF text items into visual lines, then build a blocks-style structure.
function buildBlocksFromTextContent(content, pageHeight) {
  const items = [];
  for (const it of content.items || []) {
    const str = String(it.str || '').trim();
    if (!str) continue;
    const x = it.transform[4];
    const y = it.transform[5]; // PDF origin is bottom-left
    const w = it.width || str.length * 6;
    items.push({
      text: str,
      x0: x,
      x1: x + w,
      y0: pageHeight - y, // flip to top-left
      y1: pageHeight - y + (it.height || 12),
    });
  }

  // Sort by reading order: top-to-bottom, then left-to-right
  items.sort((a, b) => a.y0 - b.y0 || a.x0 - b.x0);

  // Group into lines (items on the same baseline have ~same y)
  const lines = [];
  for (const w of items) {
    const last = lines[lines.length - 1];
    if (last && Math.abs(last.y0 - w.y0) < 4) {
      last.words.push(w);
    } else {
      lines.push({ y0: w.y0, words: [w] });
    }
  }

  for (const l of lines) {
    l.words.sort((a, b) => a.x0 - b.x0);
  }

  const text = lines.map((l) => l.words.map((w) => w.text).join(' ')).join('\n');

  return {
    text,
    blocks: [
      {
        paragraphs: [
          {
            lines: lines.map((l) => ({
              words: l.words.map((w) => ({
                text: w.text,
                bbox: { x0: w.x0, x1: w.x1, y0: w.y0, y1: w.y1 },
              })),
            })),
          },
        ],
      },
    ],
  };
}

/**
 * Extract each page's text-with-positions AND render a preview image.
 * @param {File} file - a .pdf file
 * @returns {Promise<Array<{ocrData: object, dataUrl: string, pageNum: number}>>}
 */
export async function getPdfPages(file) {
  const lib = await getPdfLib();
  const data = await file.arrayBuffer();
  const pdf = await lib.getDocument({ data }).promise;

  const pages = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);

    // 1) Extract the native text layer with positions (no OCR needed)
    const content = await page.getTextContent();
    const base = page.getViewport({ scale: 1 });
    const ocrData = buildBlocksFromTextContent(content, base.height);

    // 2) Render a preview image (scale capped so it is quick)
    let scale = 1.5;
    if (Math.max(base.width, base.height) > 2000) {
      scale = 2000 / Math.max(base.width, base.height);
    }
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    const ctx = canvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport }).promise;
    const dataUrl = canvas.toDataURL('image/png');

    pages.push({ ocrData, dataUrl, pageNum: i });
    page.cleanup();
  }

  await pdf.destroy();
  return pages;
}